<template>
  <div class="zk-form-control" :class="[$attrs.class, `zk-form-control--${type}`, { 'has-label': !!label, 'is-textarea': isTextarea, 'is-multiple-select': isMultipleSelect, 'is-cascader': isCascader }]" :style="[customStyle, $attrs.style]">
    <template v-if="!isButton && !isTag">
      <span v-if="label" class="zk-form-label">
        <span v-if="required" class="zk-form-required">*</span>
        {{ label }}
      </span>
      <span v-if="label" class="zk-form-split"></span>
      <div class="zk-form-field">
        <el-input
          v-if="isInput"
          :model-value="modelValue"
          :type="inputType"
          :size="size"
          :autosize="textareaAutosize"
          v-bind="$attrs"
          @update:modelValue="updateValue"
        >
          <template v-for="(_, name) in $slots" :key="name" #[name]="slotProps">
            <slot :name="name" v-bind="slotProps" />
          </template>
        </el-input>

        <el-select
          v-else-if="isSelect"
          :model-value="modelValue"
          :size="size"
          v-bind="$attrs"
          :popper-class="selectPopperClass"
          @update:modelValue="updateValue" 
        >
          <template v-for="(_, name) in nonDefaultSelectSlots" :key="name" #[name]="slotProps">
            <slot :name="name" v-bind="slotProps || {}"></slot>
          </template>
          
          <template #default>
            <slot v-if="$slots.default"></slot>
            <template v-else>
               <el-option
                v-for="item in options"
                :key="getOptionValue(item)"
                :label="getOptionLabel(item)"
                :value="getOptionValue(item)"
                :disabled="getOptionDisabled(item)"
              >
                <template v-if="optionTip && getOptionTip(item)">
                  <span>{{ getOptionLabel(item) }}</span>
                  <span class="zk-option-tip">（{{ getOptionTip(item) }}）</span>
                </template>
              </el-option>
            </template>
          </template>
        </el-select>

        <div v-else-if="isInputSelect" class="zk-input-select">
          <el-input
            :model-value="modelValue"
            :type="inputType"
            :size="size"
            :autosize="textareaAutosize"
            v-bind="nativeInputAttrs"
            @update:modelValue="updateValue"
          >
            <template v-if="!isTextarea" #suffix>
              <el-dropdown trigger="click" placement="bottom-end" :max-height="260" popper-class="zk-input-select-popper" @command="handlePickOption">
                <el-icon class="zk-input-select__arrow"><arrow-down /></el-icon>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item v-for="item in options" :key="getOptionValue(item)" :command="item">
                      {{ getOptionLabel(item) }}
                    </el-dropdown-item>
                    <el-dropdown-item v-if="!options || !options.length" disabled>暂无选项</el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </template>
          </el-input>
          <el-dropdown
            v-if="isTextarea"
            trigger="click"
            placement="bottom-end"
            :max-height="260"
            popper-class="zk-input-select-popper"
            @command="handlePickOption"
          >
            <el-icon class="zk-input-select__arrow zk-input-select__arrow--textarea"><arrow-down /></el-icon>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item v-for="item in options" :key="getOptionValue(item)" :command="item">
                  {{ getOptionLabel(item) }}
                </el-dropdown-item>
                <el-dropdown-item v-if="!options || !options.length" disabled>暂无选项</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>

        <el-tree-select
          v-else-if="isTreeSelect"
          :model-value="modelValue"
          :size="size"
          v-bind="$attrs"
          @update:modelValue="updateValue"
        >
          <template v-for="(_, name) in $slots" :key="name" #[name]="slotProps">
            <slot :name="name" v-bind="slotProps" />
          </template>
        </el-tree-select>

        <el-cascader
          v-else-if="isCascader"
          :model-value="modelValue"
          :options="options"
          :size="size"
          v-bind="$attrs"
          @update:modelValue="updateValue"
        >
          <template v-for="(_, name) in $slots" :key="name" #[name]="slotProps">
            <slot :name="name" v-bind="slotProps" />
          </template>
        </el-cascader>

        <el-time-picker
          v-else-if="isTime"
          :model-value="modelValue"
          :is-range="isTimeRange"
          :size="size"
          v-bind="$attrs"
          @update:modelValue="updateValue"
        >
          <template v-for="(_, name) in $slots" :key="name" #[name]="slotProps">
            <slot :name="name" v-bind="slotProps" />
          </template>
        </el-time-picker>

        <el-date-picker
          v-else
          :model-value="modelValue"
          :type="dateType"
          :size="size"
          v-bind="$attrs"
          @update:modelValue="updateValue"
        >
          <template v-for="(_, name) in $slots" :key="name" #[name]="slotProps">
            <slot :name="name" v-bind="slotProps" />
          </template>
        </el-date-picker>
      </div>
    </template>

    <button v-else-if="isButton" class="zk-form-btn" :class="`zk-form-btn--${buttonType}`" :style="buttonStyle" type="button" @click="handleClick">
      <img v-if="iconImage" :src="iconImage" alt="" class="zk-form-btn__icon zk-form-btn__icon-img" />
      <svg-icon v-else-if="buttonType === 'ai'" name="ai" class="zk-form-btn__icon" />
      <svg-icon v-else-if="icon" :name="icon" class="zk-form-btn__icon" />
      <span class="zk-form-btn__text">{{ text }}</span>
    </button>

    <span v-else-if="isTag" class="zk-form-tag" :class="[`zk-form-tag--${tagType}`, `zk-form-tag--${tagEffect}`, `zk-form-tag--${size}`, { 'is-round': round }]" :style="tagStyle" @click="handleClick">
      <slot>{{ text }}</slot>
    </span>
  </div>
</template>

<script>
import { ArrowDown } from '@element-plus/icons-vue'

export default {
  name: 'FormUtils',
  inheritAttrs: false,
  components: {
    ArrowDown
  },
  props: {
    type: {
      type: String,
      default: 'input'
    },
    label: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    text: {
      type: [String, Number],
      default: ''
    },
    icon: {
      type: String,
      default: ''
    },
    iconImage: {
      type: String,
      default: ''
    },
    buttonType: {
      type: String,
      default: 'primary'
    },
    modelValue: {
      type: [String, Number, Boolean, Array, Date],
      default: ''
    },
    inputType: {
      type: String,
      default: 'text'
    },
    options: {
      type: Array,
      default: () => []
    },
    optionLabel: {
      type: String,
      default: 'label'
    },
    optionValue: {
      type: String,
      default: 'value'
    },
    optionDisabled: {
      type: String,
      default: 'disabled'
    },
    optionTip: {
      type: String,
      default: ''
    },
    tagType: {
      type: String,
      default: 'primary'
    },
    tagColor: {
      type: String,
      default: ''
    },
    effect: {
      type: String,
      default: 'solid'
    },
    size: {
      type: String,
      default: 'default'
    },
    width: {
      type: [String, Number],
      default: ''
    },
    round: {
      type: Boolean,
      default: false
    },
    buttonHeightReduce: {
      type: Number,
      default: 0
    },
    autosize: {
      type: [Boolean, Object],
      default: undefined
    }
  },
  emits: ['update:modelValue', 'change', 'click'],
  computed: {
    customStyle() {
      const style = {}
      if (this.width) {
        style.width = isNaN(this.width) ? this.width : `${this.width}px`
      } else if (this.isSelect) {
        style.width = '200px'
      }
      return style
    },
    nonDefaultSelectSlots() {
      const slots = {}
      if (this.$slots) {
        for (const key in this.$slots) {
          if (key !== 'default') {
            slots[key] = this.$slots[key]
          }
        }
      }
      return slots
    },
    selectPopperClass() {
      const baseClass = 'zk-form-select-popper'
      const raw = this.$attrs['popper-class']
      if (!raw) {
        return baseClass
      }
      if (Array.isArray(raw)) {
        return [...raw, baseClass].filter(Boolean).join(' ')
      }
      if (typeof raw === 'object') {
        const classList = Object.keys(raw).filter(key => !!raw[key])
        return [...classList, baseClass].join(' ')
      }
      return `${String(raw)} ${baseClass}`
    },
    buttonStyle() {
      if (!this.isButton || this.buttonHeightReduce <= 0) {
        return {}
      }
      const height = Math.max(24, 36 - this.buttonHeightReduce)
      return {
        height: `${height}px`
      }
    },
    isInput() {
      return this.type === 'input'
    },
    isInputSelect() {
      return this.type === 'inputselect'
    },
    isTextarea() {
      return (this.isInput || this.isInputSelect) && this.inputType === 'textarea'
    },
    textareaAutosize() {
      if (!this.isTextarea) return undefined
      if (this.autosize !== undefined) return this.autosize
      return { minRows: 1, maxRows: 8 }
    },
    isSelect() {
      return this.type === 'select'
    },
    // inputselect 复用了下拉相关的 attr（filterable/allow-create 等）会落到原生 input 上产生告警，
    // 这里过滤掉这些下拉专用属性，只把真正的 input 属性透传下去。
    nativeInputAttrs() {
      const omitKeys = new Set([
        'filterable', 'allow-create', 'allowCreate', 'default-first-option', 'defaultFirstOption',
        'reserve-keyword', 'reserveKeyword', 'multiple'
      ])
      const attrs = {}
      Object.keys(this.$attrs || {}).forEach(key => {
        if (!omitKeys.has(key)) attrs[key] = this.$attrs[key]
      })
      return attrs
    },
    isMultipleSelect() {
      return this.isSelect && this.$attrs.multiple !== undefined && this.$attrs.multiple !== false
    },
    isTreeSelect() {
      return this.type === 'treeselect'
    },
    isCascader() {
      return this.type === 'cascader'
    },
    isButton() {
      return this.type === 'button'
    },
    isTag() {
      return this.type === 'tag'
    },
    tagEffect() {
      if (this.effect === 'light' || this.effect === 'dark') return 'solid'
      if (this.effect === 'plain') return 'outline'
      return this.effect
    },
    tagStyle() {
      if (!this.tagColor) return {}
      const rgb = this.hexToRgb(this.tagColor)
      const style = {
        '--zk-form-tag-color': this.tagColor
      }
      if (rgb) {
        style['--zk-form-tag-color-rgb'] = rgb
      }
      return style
    },
    isTime() {
      return this.type === 'time' || this.type === 'timerange'
    },
    isTimeRange() {
      return this.type === 'timerange'
    },
    dateType() {
      const dateTypes = [
        'date',
        'daterange',
        'datetime',
        'datetimerange',
        'month',
        'monthrange',
        'year',
        'week'
      ]
      return dateTypes.includes(this.type) ? this.type : 'date'
    }
  },
  methods: {
    updateValue(value) {
      this.$emit('update:modelValue', value)
      this.$emit('change', value)
    },
    handleClick(event) {
      this.$emit('click', event)
    },
    handlePickOption(item) {
      this.updateValue(this.getOptionValue(item))
    },
    getOptionLabel(item) {
      return item?.[this.optionLabel]
    },
    getOptionValue(item) {
      return item?.[this.optionValue]
    },
    getOptionDisabled(item) {
      return item?.[this.optionDisabled]
    },
    getOptionTip(item) {
      return this.optionTip ? item?.[this.optionTip] : ''
    },
    hexToRgb(color) {
      const value = String(color || '').trim()
      const match = value.match(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/)
      if (!match) return ''
      const hex = match[1].length === 3
        ? match[1].split('').map(char => char + char).join('')
        : match[1]
      const num = parseInt(hex, 16)
      return `${(num >> 16) & 255}, ${(num >> 8) & 255}, ${num & 255}`
    }
  }
}
</script>

<style scoped lang="scss">

.zk-form-control {
  --el-input-bg-color: #ffffff;
  --el-input-border-color: transparent;
  --el-input-hover-border-color: transparent;
  --el-input-focus-border-color: var(--font-info-4);
  --el-input-placeholder-color: var(--font-color-4);
  --el-input-text-color: var(--font-color-1);
  --el-border-radius-base: 8px;
  --el-input-height: 32px;
  --el-fill-color-blank: #ffffff;
  --el-border-color: transparent;

  position: relative;
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 0 8px;
  height: 36px;
  border: 1px solid #dfe6f5;
  border-radius: 10px;
  background: #ffffff;
  box-sizing: border-box;
  gap: 6px;
}

.zk-form-control.is-textarea {
  --zk-textarea-first-line-offset: 6px;
  --zk-textarea-first-line-height: 21px;
  align-items: flex-start;
  height: auto;
  padding-top: 6px;
  padding-bottom: 6px;
}

.zk-form-control.is-multiple-select {
  align-items: center;
  min-height: 36px;
  height: auto;
  padding-top: 2px;
  padding-bottom: 2px;
}

.zk-form-control.is-textarea .zk-form-label {
  align-self: flex-start;
  line-height: var(--zk-textarea-first-line-height);
  margin-top: var(--zk-textarea-first-line-offset);
}

.zk-form-control.is-textarea .zk-form-split {
  align-self: flex-start;
  height: var(--zk-textarea-first-line-height);
  margin-top: var(--zk-textarea-first-line-offset);
}

.zk-form-control.is-textarea .zk-form-required {
  top: calc(6px + var(--zk-textarea-first-line-offset));
  transform: none;
}

.zk-form-control.is-textarea .zk-form-field {
  align-items: stretch;
}

.zk-form-control.is-multiple-select .zk-form-field {
  align-items: stretch;
}

.zk-form-control.is-textarea :deep(.el-textarea__inner) {
  min-height: var(--zk-textarea-first-line-height);
  line-height: var(--zk-textarea-first-line-height);
  padding: var(--zk-textarea-first-line-offset) 8px 6px;
  resize: vertical;
}

.zk-form-control--button,
.zk-form-control--tag {
  padding: 0;
  border: none;
  height: auto;
  background: transparent;
}

.zk-form-control.has-label {
  padding-left: 12px;
}

.zk-form-label {
  font-size: 14px;
  color: var(--font-color-2);
  white-space: nowrap;
}

.zk-form-required {
  position: absolute;
  left: 4px;
  top: 50%;
  transform: translateY(-50%);
  color: #f56c6c;
  line-height: 1;
}

.zk-form-split {
  width: 1px;
  height: 18px;
  background: var(--bg-color-2);
  margin: 0 6px 0 2px;
}

.zk-form-field {
  display: inline-flex;
  align-items: center;
  min-width: 120px;
  width: 100%;
  min-width: 0;
  flex: 1;
}

.zk-form-field :deep(.el-input),
.zk-form-field :deep(.el-select),
.zk-form-field :deep(.el-tree-select),
.zk-form-field :deep(.el-cascader),
.zk-form-field :deep(.el-date-editor),
.zk-form-field :deep(.el-time-editor) {
  width: 100%;
  min-width: 0;
}

.zk-form-control :deep(.el-select__selected-item) {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

:deep(.zk-form-select-popper.el-select-dropdown) {
  max-width: min(90vw, 960px);
}

:deep(.zk-form-select-popper .el-select-dropdown__item) {
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.4;
  height: auto;
  min-height: 34px;
  padding-top: 6px;
  padding-bottom: 6px;
}

.zk-form-control :deep(.el-input__wrapper),
.zk-form-control :deep(.el-select__wrapper),
.zk-form-control :deep(.el-date-editor .el-input__wrapper),
.zk-form-control :deep(.el-time-editor .el-input__wrapper) {
  border-radius: 8px;
  box-shadow: none;
  background: #ffffff;
  padding: 0 4px;
}

.zk-form-control.is-multiple-select :deep(.el-select__wrapper) {
  min-height: 28px;
  height: auto;
  align-items: center;
  padding-top: 2px;
  padding-bottom: 2px;
}

.zk-form-control.is-multiple-select :deep(.el-select__selection) {
  align-items: center;
  row-gap: 4px;
}

.zk-form-control.is-multiple-select :deep(.el-select__caret) {
  align-self: center;
}

.zk-form-control :deep(.el-input__wrapper.is-focus),
.zk-form-control :deep(.el-select__wrapper.is-focus) {
  box-shadow: none;
}

// 级联选择器：隐藏内部 el-input 自身的框体（边框/背景/圆角），只保留外层容器的一个框，避免显示两个框体；
// hover/focus 状态同样不加内框，宽度撑满使文本区与外层框对齐。
.zk-form-control.is-cascader :deep(.el-cascader),
.zk-form-control.is-cascader :deep(.el-cascader .el-input) {
  width: 100%;
  min-width: 0;
}

.zk-form-control.is-cascader :deep(.el-cascader .el-input__wrapper) {
  background: transparent;
  border-radius: 0;
  padding: 0 4px;
  box-shadow: none;
}

.zk-form-control.is-cascader :deep(.el-cascader .el-input__wrapper:hover),
.zk-form-control.is-cascader :deep(.el-cascader .el-input__wrapper.is-focus) {
  box-shadow: none;
}

.zk-form-control :deep(.el-input__inner),
.zk-form-control :deep(.el-range-input) {
  font-size: 14px;
}

.zk-form-control :deep(.el-input__inner::placeholder),
.zk-form-control :deep(.el-textarea__inner::placeholder),
.zk-form-control :deep(.el-select__placeholder) {
  font-size: 14px;
}

.zk-form-control :deep(.el-range-input) {
  background: #ffffff;
}

.zk-form-control :deep(.el-input__prefix-inner),
.zk-form-control :deep(.el-input__suffix-inner) {
  color: var(--font-color-3);
}

.zk-input-select {
  position: relative;
  width: 100%;
}

.zk-input-select__arrow {
  cursor: pointer;
  color: var(--font-color-3);
  font-size: 14px;
  transition: transform 0.2s ease;
}

.zk-input-select__arrow:hover {
  color: var(--font-info-4);
}

.zk-input-select__arrow--textarea {
  position: absolute;
  right: 8px;
  top: 8px;
  z-index: 2;
  padding: 2px;
  background: #ffffff;
}

.zk-form-control.is-textarea .zk-input-select :deep(.el-textarea__inner) {
  padding-right: 28px;
}

.zk-form-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 16px;
  box-sizing: border-box;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
  color: var(--font-color-5);
}

.zk-form-btn--primary {
  background: var(--font-info-4);
}

.zk-form-btn--info {
  background: var(--font-color-3);
}

.zk-form-btn--success {
  background: var(--font-info-1);
}

.zk-form-btn--warning {
  background: var(--font-info-5);
}

.zk-form-btn--danger {
  background: var(--font-info-6);
}

.zk-form-btn--outline-gray {
  background: #ffffff;
  border: 1px solid #dddddd;
  color: #666666;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 14px;
}

.zk-form-btn--ai {
  background: #ffffff;
  color: var(--font-info-4);
  border: 1px solid transparent;
  background-clip: padding-box, border-box;
  background-origin: padding-box, border-box;
  background-image: linear-gradient(to right, #ffffff, #ffffff), linear-gradient(to right, #6FD2FF, #7D72FF);
  border-radius: 12px;
  padding: 6px 16px;
}

.zk-form-btn--ai .zk-form-btn__icon {
  font-size: 18px;
}

.zk-form-btn--ai:hover {
  filter: brightness(0.95);
}

.zk-form-btn--primary:hover {
  background: var(--bg-info-4);
}

.zk-form-btn--info:hover {
  background: var(--font-color-4);
}

.zk-form-btn--success:hover {
  background: var(--bg-info-1);
}

.zk-form-btn--warning:hover {
  background: var(--bg-info-5);
}

.zk-form-btn--danger:hover {
  background: var(--bg-info-6);
}

.zk-form-btn--outline-gray:hover {
  border-color: #d0d0d0;
  color: #666666;
  background: #ffffff;
}

.zk-form-btn:hover {
  filter: brightness(0.98);
}

.zk-form-btn__icon {
  font-size: 16px;
}

.zk-form-btn__icon-img {
  width: 18px;
  height: 18px;
  object-fit: contain;
}

.zk-form-tag {
  --zk-form-tag-color: var(--font-info-3);
  --zk-form-tag-color-rgb: 0, 166, 243;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  min-width: 0;
  height: 20px;
  padding: 0 7px;
  font-size: 10px !important;
  line-height: 18px;
  border: 1px solid transparent;
  border-radius: 10px;
  box-sizing: border-box;
  white-space: nowrap;
}

.zk-form-tag.is-round {
  border-radius: 10px;
}

.zk-form-tag--large {
  height: 20px;
  padding: 0 7px;
  font-size: 10px !important;
}

.zk-form-tag--small {
  height: 20px;
  padding: 0 7px;
  font-size: 10px !important;
}

.zk-form-tag--success, .zk-form-tag--1 {
  --zk-form-tag-color: var(--font-info-1);
  --zk-form-tag-color-rgb: 97, 183, 3;
}
.zk-form-tag--info, .zk-form-tag--2 {
  --zk-form-tag-color: var(--font-info-2);
  --zk-form-tag-color-rgb: 27, 195, 156;
}
.zk-form-tag--primary, .zk-form-tag--3 {
  --zk-form-tag-color: var(--font-info-3);
  --zk-form-tag-color-rgb: 0, 166, 243;
}
.zk-form-tag--4 {
  --zk-form-tag-color: var(--font-info-4);
  --zk-form-tag-color-rgb: 125, 114, 255;
}
.zk-form-tag--warning, .zk-form-tag--5 {
  --zk-form-tag-color: var(--font-info-5);
  --zk-form-tag-color-rgb: 255, 163, 91;
}
.zk-form-tag--danger, .zk-form-tag--6 {
  --zk-form-tag-color: var(--font-info-6);
  --zk-form-tag-color-rgb: 255, 116, 99;
}
.zk-form-tag--7 {
  --zk-form-tag-color: var(--font-info-7);
  --zk-form-tag-color-rgb: 153, 153, 153;
}
.zk-form-tag--8 {
  --zk-form-tag-color: #2F80ED;
  --zk-form-tag-color-rgb: 47, 128, 237;
}
.zk-form-tag--9 {
  --zk-form-tag-color: #A855F7;
  --zk-form-tag-color-rgb: 168, 85, 247;
}

.zk-form-tag--solid {
  color: #fff;
  background-color: var(--zk-form-tag-color);
  border-color: var(--zk-form-tag-color);
}

.zk-form-tag--outline {
  color: var(--zk-form-tag-color);
  background-color: rgba(var(--zk-form-tag-color-rgb), 0.1);
  border-color: rgba(var(--zk-form-tag-color-rgb), 0.3);
}
</style>

<style lang="scss">
.zk-form-select-popper.el-select-dropdown {
  max-width: min(420px, calc(100vw - 32px)) !important;
}

.zk-form-select-popper .el-select-dropdown__wrap {
  max-height: 280px;
}

.zk-form-select-popper .el-select-dropdown__item {
  white-space: normal !important;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.4;
  height: auto !important;
  min-height: 34px;
  padding-top: 6px;
  padding-bottom: 6px;
}

.zk-form-select-popper .el-select-dropdown__item span {
  white-space: normal !important;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.zk-form-select-popper .el-select-dropdown__item .zk-option-tip {
  color: #9aa1ae;
}

.zk-input-select-popper.el-dropdown__popper {
  max-width: min(420px, calc(100vw - 32px));
}

.zk-input-select-popper .el-dropdown-menu__item {
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.4;
  max-width: 400px;
}
</style>
